import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { ArrowLeft, Package, Tag, ShoppingBag, MapPin, Globe, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#0d0c0a", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", purple: "#7C3AED",
};
const F = { cond: "'Saira Condensed', sans-serif", sans: "'DM Sans', 'Outfit', sans-serif", brush: "'Permanent Marker', cursive" };

export default function MarketProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await http.get(`/marker-collection/public/market/products/${slug}`);
        setProduct(data);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-12 h-12 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div>
  );

  if (notFound || !product) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: C.bg, color: C.text }}>
      <Package size={48} style={{ color: `${C.gold}30` }} />
      <h1 className="text-3xl font-black" style={{ fontFamily: F.cond }}>Product Not Found</h1>
      <Link to="/the-market" className="text-sm underline" style={{ color: C.gold }}>Back to The Market</Link>
    </div>
  );

  const outOfStock = product.quantity != null && product.quantity <= 0;
  const img = product.main_image ? resolveImage(product.main_image) : null;
  const hasSale = product.on_sale && product.sale_price > 0 && product.sale_price < product.price;
  const displayPrice = hasSale ? product.sale_price : (product.price || 0);
  const location = product.location || "The Village Antique and Vendor Market, Kingsport, TN — Booth I-12";

  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-6 pt-6">
        <Link to="/the-market" className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase transition-colors hover:text-[#C9A055]"
          style={{ fontFamily: F.sans, color: C.text2 }} data-testid="market-detail-back">
          <ArrowLeft size={14} /> Back to The Market
        </Link>
      </div>

      {/* Product Layout */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-testid="market-detail-layout">
          {/* Image */}
          <div className="relative aspect-square overflow-hidden border" style={{ background: C.bg2, borderColor: "rgba(255,255,255,0.06)" }}>
            {img ? (
              <img src={img} alt={product.title} className="w-full h-full object-cover" data-testid="market-detail-image" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package size={64} style={{ color: `${C.gold}15` }} />
              </div>
            )}
            {/* Category badge */}
            <div className="absolute top-3 left-3 px-3 py-1" style={{ background: "rgba(124,58,237,0.9)" }}>
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-white" style={{ fontFamily: F.sans }}>
                {product.category_name}
              </span>
            </div>
            {/* Sale badge */}
            {hasSale && (
              <div className="absolute top-3 right-3 px-3 py-1" style={{ background: "#FF3B30" }}>
                <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-white" style={{ fontFamily: F.sans }}>SALE</span>
              </div>
            )}
            {outOfStock && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }}>
                <span className="text-2xl tracking-[0.3em] uppercase font-black" style={{ fontFamily: F.cond }}>SOLD OUT</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col">
            {/* Vendor code */}
            {product.vendor_code && (
              <span className="text-[10px] font-mono tracking-wider mb-2" style={{ color: C.gold }} data-testid="market-detail-vendor">
                {product.vendor_code}
              </span>
            )}

            <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[0.95]"
              style={{ fontFamily: F.cond }} data-testid="market-detail-title">
              {product.title}
            </h1>

            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1"
                style={{ background: `${C.purple}20`, color: C.purple, fontFamily: F.sans }}>
                {product.category_name}
              </span>
              {product.available_online && (
                <span className="text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 flex items-center gap-1"
                  style={{ background: "rgba(16,185,129,0.15)", color: "#10b981", fontFamily: F.sans }}
                  data-testid="market-detail-online-badge">
                  <Globe size={10} /> Shop Online
                </span>
              )}
              {product.quantity != null && product.quantity > 0 && (
                <span className="text-[10px] tracking-[0.15em]" style={{ fontFamily: F.sans, color: C.text2 }}>
                  {product.quantity} available
                </span>
              )}
            </div>

            {/* Price */}
            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-5xl font-black" style={{ fontFamily: F.cond, color: hasSale ? "#FF3B30" : C.gold }} data-testid="market-detail-price">
                ${Number(displayPrice).toFixed(2)}
              </span>
              {hasSale && (
                <span className="text-2xl font-bold line-through" style={{ fontFamily: F.cond, color: "#999" }} data-testid="market-detail-original-price">
                  ${Number(product.price).toFixed(0)}
                </span>
              )}
            </div>

            {/* Tags */}
            {(product.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4" data-testid="market-detail-tags">
                {product.tags.map(t => (
                  <Link key={t} to={`/the-market`}
                    className="px-3 py-1 text-[9px] tracking-[0.15em] uppercase border transition-colors hover:border-purple-500/50"
                    style={{ fontFamily: F.sans, background: `${C.purple}15`, color: `${C.purple}cc`, borderColor: `${C.purple}25` }}>
                    {t}
                  </Link>
                ))}
              </div>
            )}

            {product.description && (
              <div className="mt-6 pt-6" style={{ borderTop: `1px solid rgba(255,255,255,0.08)` }}>
                <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }} data-testid="market-detail-description">
                  {product.description}
                </p>
              </div>
            )}

            {/* Buy Online Section */}
            {product.available_online && !outOfStock && (
              <BuyOnlineSection product={product} displayPrice={displayPrice} />
            )}

            {/* Location */}
            <div className="mt-auto pt-8">
              <div className="p-4 border" style={{ background: `${C.gold}08`, borderColor: `${C.gold}20` }}>
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="flex-shrink-0 mt-0.5" style={{ color: C.gold }} />
                  <div>
                    <p className="text-[10px] tracking-[0.15em] font-bold uppercase mb-1" style={{ fontFamily: F.sans, color: C.gold }} data-testid="market-detail-location-label">
                      Find It Here
                    </p>
                    <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }} data-testid="market-detail-location">
                      {location}
                    </p>
                    {product.available_online && (
                      <p className="text-[10px] mt-2" style={{ fontFamily: F.sans, color: "#10b981" }}>
                        Also available for online purchase above.
                      </p>
                    )}
                    {!product.available_online && (
                      <p className="text-[10px] mt-2 tracking-[0.1em]" style={{ fontFamily: F.sans, color: C.text2 }}>
                        Available at markets and events. Follow us on social media or visit{" "}
                        <span style={{ color: C.gold }}>MarkerCollection.com</span> for upcoming dates and locations.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back CTA */}
      <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-5xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/the-market"
            className="px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold border transition-colors hover:bg-[#C9A055]/10"
            style={{ fontFamily: F.sans, color: C.gold, borderColor: `${C.gold}40` }}>
            Browse The Market
          </Link>
          <Link to="/marker-collection"
            className="px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold border transition-colors hover:bg-white/5"
            style={{ fontFamily: F.sans, color: C.text2, borderColor: "rgba(255,255,255,0.1)" }}>
            View Original Art
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ─── Buy Online Section ─── */
function BuyOnlineSection({ product, displayPrice }) {
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [payMethod, setPayMethod] = useState("venmo");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const total = (displayPrice * qty).toFixed(2);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      await http.post("/marker-collection/market-order", {
        product_id: product.id,
        product_title: product.title,
        vendor_code: product.vendor_code,
        quantity: qty,
        unit_price: displayPrice,
        total: Number(total),
        customer_name: name.trim(),
        customer_email: email.trim(),
        payment_method: payMethod,
      });
      setSuccess(true);
      toast.success("Order placed!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally { setSubmitting(false); }
  };

  if (success) return (
    <div className="mt-6 p-5 border border-emerald-500/30 bg-emerald-900/10 text-center" data-testid="market-order-success">
      <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400" />
      <p className="text-sm font-bold" style={{ fontFamily: F.sans, color: "#10b981" }}>Order Received!</p>
      <p className="text-xs mt-1" style={{ fontFamily: F.sans, color: C.text2 }}>We'll reach out with payment and pickup/shipping details.</p>
    </div>
  );

  return (
    <div className="mt-6 p-5 border" style={{ borderColor: "rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.05)" }} data-testid="market-buy-online">
      <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-4 flex items-center gap-2" style={{ fontFamily: F.sans, color: "#10b981" }}>
        <ShoppingBag size={14} /> Buy Online
      </p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input className="stc-input w-full text-[12px]" placeholder="Your Name *" value={name}
            onChange={e => setName(e.target.value)} data-testid="market-buy-name" />
          <input className="stc-input w-full text-[12px]" placeholder="Email *" value={email}
            onChange={e => setEmail(e.target.value)} data-testid="market-buy-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-ui text-[8px] tracking-[0.2em] text-[#8a857d] block mb-1">QTY</label>
            <input type="number" min="1" max={product.quantity || 99} className="stc-input w-full text-[12px] font-mono" value={qty}
              onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))} data-testid="market-buy-qty" />
          </div>
          <div>
            <label className="font-ui text-[8px] tracking-[0.2em] text-[#8a857d] block mb-1">PAY VIA</label>
            <select className="stc-input w-full text-[12px]" value={payMethod} onChange={e => setPayMethod(e.target.value)} data-testid="market-buy-method">
              <option value="venmo">Venmo</option>
              <option value="cashapp">Cash App</option>
              <option value="stripe">Card (Stripe)</option>
              <option value="pickup_cash">Cash at Pickup</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <span className="text-2xl font-black" style={{ fontFamily: F.cond, color: C.gold }}>${total}</span>
          <button onClick={handleSubmit} disabled={submitting}
            className="px-6 py-3 text-[10px] tracking-[0.2em] uppercase font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
            style={{ fontFamily: F.sans, background: "#10b981", color: "#fff" }}
            data-testid="market-buy-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
            Place Order
          </button>
        </div>
      </div>
    </div>
  );
}
